/**
 * Lazy panel registry — one `React.lazy` import per tab in `ALL_TABS`,
 * paired with a typed prop projection that lifts only the slices each
 * panel needs out of the dashboard payload.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { lazy } from "react";
import type { DashboardData } from "../hooks/useData";

export const panels = {
  play: lazy(async () =>
    import("../components/WitnessedFirePanel").then((m) => ({
      default: m.WitnessedFirePanel,
    })),
  ),
  claims: lazy(async () =>
    import("../components/ClaimsPanel").then((m) => ({
      default: m.ClaimsPanel,
    })),
  ),
  path: lazy(async () =>
    import("../components/PathExplorer").then((m) => ({
      default: m.PathExplorer,
    })),
  ),
  delta: lazy(async () =>
    import("../components/DeltaDiffPanel").then((m) => ({
      default: m.DeltaDiffPanel,
    })),
  ),
  whatif: lazy(async () =>
    import("../components/WhatIfSimulator").then((m) => ({
      default: m.WhatIfSimulator,
    })),
  ),
  proposals: lazy(async () =>
    import("../components/ProposalPanel").then((m) => ({
      default: m.ProposalPanel,
    })),
  ),
  audit: lazy(async () =>
    import("../components/AuditPanel").then((m) => ({ default: m.AuditPanel })),
  ),
  snapshots: lazy(async () =>
    import("../components/SnapshotPanel").then((m) => ({
      default: m.SnapshotPanel,
    })),
  ),
  region: lazy(async () =>
    import("../components/RegimeRegionPanel").then((m) => ({
      default: m.RegimeRegionPanel,
    })),
  ),
  receipt: lazy(async () =>
    import("../components/ReceiptExplainerPanel").then((m) => ({
      default: m.ReceiptExplainerPanel,
    })),
  ),
  proof: lazy(async () =>
    import("../components/ProofTreePanel").then((m) => ({
      default: m.ProofTreePanel,
    })),
  ),
  "matrix-immunity": lazy(
    async () => import("../components/MatrixImmunityPanel"),
  ),
  "certified-manifold": lazy(
    async () => import("../components/CertifiedManifoldPanel"),
  ),
  "governance-health": lazy(
    async () => import("../components/GovernanceHealthPanel"),
  ),
  conservation: lazy(async () => import("../components/ConservationPanel")),
  "regime-transition": lazy(
    async () => import("../components/RegimeTransitionPanel"),
  ),
  "diagonal-torus": lazy(
    async () => import("../components/DiagonalTorusPanel"),
  ),
  "policy-schedule": lazy(
    async () => import("../components/PolicySchedulePanel"),
  ),
  "fleet-comparison": lazy(
    async () => import("../components/FleetComparisonPanel"),
  ),
  "bridge-lifecycle": lazy(
    async () => import("../components/BridgeLifecyclePanel"),
  ),
  "segal-verify": lazy(
    async () => import("../components/SegalVerificationPanel"),
  ),
  "diagram-gallery": lazy(
    async () => import("../components/DiagramGalleryPanel"),
  ),
  "diff-structure": lazy(
    async () => import("../components/DifferentialStructurePanel"),
  ),
  "ml-governance": lazy(async () => import("../components/MLGovernancePanel")),
  "fleet-security": lazy(
    async () => import("../components/FleetSecurityPanel"),
  ),
  scenario: lazy(async () => import("../components/ScenarioWalkthroughPanel")),
  scoring: lazy(async () => import("../components/ScoringConformancePanel")),
} as const;

export type PanelKey = keyof typeof panels;

export function isLazyPanelKey(key: string): key is PanelKey {
  return key in panels;
}

/** Per-tab prop selectors. Each entry returns just the slice of the
 *  dashboard data its panel needs. Keeping the map centralized makes
 *  the per-tab data contract explicit without threading a giant
 *  `Record<string, unknown>` through the renderer. */
const propSelectors = {
  path: (data) => ({ matrix: data.matrix }),
  delta: (data) => ({ diff: data.deltaDiff }),
  whatif: (data) => ({
    bridges: data.bridges,
    matrix: data.matrix,
    missingBridge: data.missingBridge,
  }),
  region: (data) => ({
    footprint: data.footprint,
    regions: data.regions,
  }),
  receipt: (data) => ({ receipt: data.receipt }),
  proof: (data) => ({ tree: data.proofTree }),
} as const satisfies Partial<Record<PanelKey, (data: DashboardData) => object>>;

export function buildPanelProps(tab: PanelKey, data: DashboardData): object {
  const selector = (
    propSelectors as Record<string, undefined | ((d: DashboardData) => object)>
  )[tab];
  return selector ? selector(data) : {};
}

export function PanelFallback(): React.JSX.Element {
  return (
    <div className="flex items-center justify-center h-32 text-slate-600 text-sm animate-pulse">
      Loading…
    </div>
  );
}
